/**
 * Constantes partagées pour l'affichage des prix et fonctionnalités.
 * Utilisé par DynamicPricing (landing) et PricingPage (/pricing).
 */

// Feature labels for display (i18n-ready)
export const FEATURE_LABELS: Record<string, Record<string, string>> = {
  fr: {
    basic_chat: 'Chat IA de base',
    rag: 'RAG - Base de connaissances',
    mcp_tools: 'Outils MCP',
    api_access: 'Accès API',
    whatsapp: 'Intégration WhatsApp',
    workflows: 'Workflows automatisés',
    priority_support: 'Support prioritaire',
    custom_branding: 'Branding personnalisé',
    sso: 'SSO / SAML',
    dedicated_infra: 'Infrastructure dédiée',
    sla_guarantee: 'Garantie SLA',
    advanced_analytics: 'Analytics avancés',
    widget_embed: 'Widget intégrable',
    remove_branding: 'Sans branding Totemium',
    analytics: 'Analytics',
  },
  en: {
    basic_chat: 'Basic AI Chat',
    rag: 'RAG - Knowledge Base',
    mcp_tools: 'MCP Tools',
    api_access: 'API Access',
    whatsapp: 'WhatsApp Integration',
    workflows: 'Automated Workflows',
    priority_support: 'Priority Support',
    custom_branding: 'Custom Branding',
    sso: 'SSO / SAML',
    dedicated_infra: 'Dedicated Infrastructure',
    sla_guarantee: 'SLA Guarantee',
    advanced_analytics: 'Advanced Analytics',
    widget_embed: 'Embeddable Widget',
    remove_branding: 'No Totemium Branding',
    analytics: 'Analytics',
  },
};

// Limit labels for display
export const LIMIT_LABELS: Record<string, Record<string, string>> = {
  fr: {
    agents: 'agent IA',
    agents_plural: 'agents IA',
    conversations: 'conversations/mois',
    knowledge: 'documents',
    storage: 'Mo stockage',
  },
  en: {
    agents: 'AI agent',
    agents_plural: 'AI agents',
    conversations: 'conversations/month',
    knowledge: 'documents',
    storage: 'MB storage',
  },
};

// Helper to get feature label with fallback
export function getFeatureLabel(feature: string, lang: string = 'fr'): string {
  return FEATURE_LABELS[lang]?.[feature] || FEATURE_LABELS['fr']?.[feature] || feature;
}

// Helper to format limits as feature strings
export interface PlanLimits {
  max_agents: number;
  max_conversations_per_month: number;
  max_knowledge_items: number;
  max_file_size_mb?: number;
  max_storage_mb?: number;
  max_crawled_pages?: number;
  max_api_calls_per_month?: number;
}

export function formatLimitsAsFeatures(limits: PlanLimits, lang: string = 'fr'): string[] {
  const labels = LIMIT_LABELS[lang] || LIMIT_LABELS['fr'];

  const features = [
    `${limits.max_agents} ${limits.max_agents > 1 ? labels.agents_plural : labels.agents}`,
    `${limits.max_conversations_per_month.toLocaleString()} ${labels.conversations}`,
    `${limits.max_knowledge_items} ${labels.knowledge}`,
  ];

  // Only add storage if defined
  if (limits.max_storage_mb != null) {
    features.push(`${limits.max_storage_mb} ${labels.storage}`);
  }

  return features;
}
